import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useFCM, isPushOptedOut, wasPushPrimerShown, markPushPrimerShown } from "../hooks/useFCM";
import { nativePushPermission } from "../lib/native";
import { isNativeApp, nativePlatform } from "../lib/platform";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Первый вход в Android-приложении: коротко объясняем, зачем нужны уведомления,
 * и сразу после этого показываем системный запрос Android 13+.
 * Показывается один раз на устройство; если клиент отказался или позже выключил
 * push в профиле — больше не появляется.
 */
export default function PushPrimer() {
  const { user } = useAuth();
  const fcm = useFCM(user);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!isNativeApp() || nativePlatform() !== "android") return;
    if (wasPushPrimerShown() || isPushOptedOut()) return;
    let alive = true;
    const timer = setTimeout(async () => {
      // "prompt" — системное разрешение ещё не спрашивали (Android 13+).
      // На Android 12 и ниже состояние сразу "granted": подписка уже
      // включилась сама, объяснять нечего.
      const perm = await nativePushPermission();
      if (alive && perm === "prompt") setOpen(true);
    }, 1200);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!open) return null;

  const allow = async () => {
    setBusy(true);
    markPushPrimerShown();
    try {
      // Нажатие на кнопку = согласие на уведомления ANGIOLINI
      await axios.post(`${API}/me/notification-consent`, { given: true }, { withCredentials: true });
    } catch { /* подписку можно включить позже в профиле */ }
    await fcm.enable();          // системный диалог Android + регистрация токена
    setBusy(false);
    setOpen(false);
  };

  const later = () => {
    markPushPrimerShown();       // навязчиво больше не спрашиваем
    setOpen(false);
  };

  return createPortal(
    <div data-testid="push-primer-overlay"
      style={{ position: "fixed", inset: 0, zIndex: 120, display: "flex",
        alignItems: "flex-end", justifyContent: "center",
        background: "rgba(42, 18, 14, 0.55)", backdropFilter: "blur(6px)" }}>
      <div data-testid="push-primer-card"
        style={{ width: "100%", maxWidth: "430px", background: "var(--ang-surface)",
          borderTop: "1px solid var(--ang-gold-line)",
          borderRadius: "18px 18px 0 0", padding: "26px 22px calc(26px + env(safe-area-inset-bottom))",
          boxShadow: "0 -12px 40px rgba(74,20,16,0.28)" }}>
        <div style={{ width: "46px", height: "46px", borderRadius: "50%",
          background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-line)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--ang-gold)", marginBottom: "16px" }}>
          <Bell size={20} />
        </div>

        <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", lineHeight: 1.15,
          color: "var(--ang-text)", margin: "0 0 10px", fontWeight: 600 }}>
          Уведомления ANGIOLINI
        </h2>
        <p style={{ fontFamily: "Manrope", fontSize: "13px", lineHeight: 1.5,
          color: "var(--ang-text-soft)", margin: "0 0 22px" }}>
          Включите уведомления, чтобы узнавать о новых коллекциях, акциях, VIP-предложениях
          и событиях бутика. Отключить можно в любой момент в профиле.
        </p>

        <button data-testid="push-primer-allow" onClick={allow} disabled={busy}
          style={{ width: "100%", background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))",
            color: "#FBF2E2", border: "none", borderRadius: "8px", padding: "14px",
            fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.1em",
            textTransform: "uppercase", cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}>
          {busy ? "Подключаем..." : "Включить уведомления"}
        </button>
        <button data-testid="push-primer-later" onClick={later} disabled={busy}
          style={{ width: "100%", background: "transparent", border: "none", marginTop: "10px",
            padding: "12px", fontFamily: "Manrope", fontSize: "12px",
            color: "var(--ang-text-muted)", cursor: "pointer" }}>
          Не сейчас
        </button>
      </div>
    </div>,
    document.body
  );
}
